import {
  initialize as initializeLD,
  type LDClient,
  type LDContext,
} from 'launchdarkly-js-client-sdk'

import { type User } from '@webapp/models'
import { Teams } from './enums'
import {
  type FeatureFlagConfig,
  type FeatureFlagKey,
  type FeatureFlags,
} from './types'

export const FEATURE_FLAGS_CONFIG: FeatureFlagConfig = {
  // GRANDFATHERED
  CHQ_34_ADD_AMZNPO_TAX_SHIP: [false, Teams.GRANDFATHERED],
  SY_1803_PUNCHOUT_ON_BEHALF_OF: [false, Teams.GRANDFATHERED],
  FT_4880_ENABLE_INTERCOM: [false, Teams.GRANDFATHERED],
  FT_4280_BILL_PAYMENT_CONSOLIDATION: [false, Teams.GRANDFATHERED],

  // Team AP
  FT_7515_FIX_CREATE_BILL_PERMISSION_ACCESS: [false, Teams.TEAM_AP],
  AP_10029_UNBILLED_ITEMS_PAGE_MIGRATION: [false, Teams.TEAM_AP],
  PAY_2953_IMPROVE_BILL_FILTERING: [false, Teams.TEAM_AP],
  AP_10171_AP_INLINE_EDITS: [false, Teams.TEAM_AP],
  AP_10285_EDITABLE_TAX_AMOUNT: [false, Teams.TEAM_AP],
  AP_10424_SIDE_BY_SIDE_VIEW: [false, Teams.TEAM_AP],

  // Team CLC
  CL_1058_AP_GRANULAR_PERMISSIONS: [false, Teams.TEAM_CLC],
  CL_1324_NEW_NO_PERMISSION_PAGE: [false, Teams.TEAM_CLC],

  // Team Pay
  PAY_2700_INTERNATIONAL_PAYMENTS: [false, Teams.TEAM_PAYMENTS],
  PAY_3067_ENHANCE_MILEAGE_EXPENSE_ITEMS: [false, Teams.TEAM_PAYMENTS],
  PAY_3401_ENABLE_AWX_MIGRATION_BANNER: [false, Teams.TEAM_PAYMENTS],
  PAY_24_CANADIAN_SPENDING_CARDS: [false, Teams.TEAM_PAYMENTS],
  PAY_3370_BULK_APPROVE_PAYMENTS: [false, Teams.TEAM_PAYMENTS],
  PAY_3569_BULK_PREPARE_PAYMENT: [false, Teams.TEAM_PAYMENTS],
  PAY_3736_DISABLE_STRIPE_SPEND_CARD: [false, Teams.TEAM_PAYMENTS],

  // Team Integrations
  INT_1976_MIGRATE_MANAGE_USERS_AND_PERMISSIONS: [
    false,
    Teams.TEAM_INTEGRATIONS,
  ],

  // Team Platform
  PLAT_2155_MERGE_NETSUITE: [false, Teams.TEAM_PLATFORM],
  PLAT_4759_NETSUITE_SUBSIDIARY_BY_LOCATION: [false, Teams.TEAM_PLATFORM],
  PLAT_4001_PUSH_PO_NUMBER_ACCT_INTEGRATION: [false, Teams.TEAM_PLATFORM],
  PLAT_4870_MULTI_ENTITY: [false, Teams.TEAM_PLATFORM],

  // Team Purchasing
  CT_700_CREATE_PO_SHIPPING_ADDRESS_USE_REQUESTER_NAME: [
    false,
    Teams.TEAM_PURCHASING,
  ],
  RFO_AUTOMATION: [false, Teams.TEAM_PURCHASING],
  FT_5817_ENABLE_BUDGETS_REDESIGN: [false, Teams.TEAM_PURCHASING],
  FT_5815_ENABLE_RECEIVED_CATEGORY: [false, Teams.TEAM_PURCHASING],
  PUR_3039_ORDER_STATUS_TRACKER_UPCOMING_APPROVALS: [
    false,
    Teams.TEAM_PURCHASING,
  ],
  PUR_3180_NEXT_GEN_INTAKE_REDESIGN: [false, Teams.TEAM_PURCHASING],
  PUR_3723_SEQUENTIAL_ORDER_NUMBER: [false, Teams.TEAM_PURCHASING],
  XP_78_LIST_COUNT_SUMMARY: [false, Teams.TEAM_PURCHASING],
  PUR_3076_FIX_PO_TAX_CALCULATION: [false, Teams.TEAM_PURCHASING],

  // Team Data
  FT_2274_ENABLE_SPEND_INSIGHTS_DASHBOARD: [false, Teams.TEAM_DATA],
  DATA_380_ENABLE_SPEND_ANALYTICS_SALES_DEMO: [false, Teams.TEAM_DATA],
  DATA_ENABLE_SPEND_INSIGHTS_BETA_FEATURES: [false, Teams.TEAM_DATA],
  DATA_1808_ENABLE_THOUGHTSPOT_BUDGET: [false, Teams.TEAM_DATA],
  DATA_1439_GLOBAL_SEARCH: [false, Teams.TEAM_DATA],
  DATA_2061_GUIDED_INTAKE_ORDERS: [false, Teams.TEAM_DATA],

  // Team Architecture
  SEGMENT_USE_UNIQUE_ID: [false, Teams.TEAM_ARCHITECTURE],
  ARCH_468_MIGRATION_SETTINGS_UNIT: [false, Teams.TEAM_ARCHITECTURE],
  ARCH_514_MIGRATION_EXPENSE_APPROVALS: [false, Teams.TEAM_ARCHITECTURE],
  ARCH_531_MIGRATION_EXPENSE_DETAILS: [false, Teams.TEAM_ARCHITECTURE],
  ARCH_544_MIGRATION_EXPENSE_REQUEST: [false, Teams.TEAM_ARCHITECTURE],
  // Team Design System

  // Team Vendors
  VND_6_VENDOR_PORTAL_INVITES: [false, Teams.TEAM_VENDORS],
}

export const FEATURE_FLAG_DEFAULTS = Object.keys(FEATURE_FLAGS_CONFIG).reduce(
  (obj, key) => {
    const [defaultValue] = FEATURE_FLAGS_CONFIG[key as FeatureFlagKey]

    return {
      [key]: defaultValue,
      ...obj,
    }
  },
  {}
) as FeatureFlags

interface FeatureFlagServiceConfig {
  clientId: string
  user: User
  domain: string
}

export class FeatureFlagService {
  private static instance: FeatureFlagService | null = null
  private readonly client: LDClient
  private readonly clientId: string
  private readonly options: LDContext
  initialized: boolean = false

  private constructor(config: FeatureFlagServiceConfig) {
    const { clientId, user, domain } = config

    this.clientId = clientId

    this.options = {
      key: 'aa0ceb',
      firstName: user?.firstName,
      lastName: user?.lastName,
      email: user?.email,
      custom: {
        domain,
      },
    }

    this.client = initializeLD(this.clientId, this.options, {
      sendEventsOnlyForVariation: true,
      streaming: true,
    })
  }

  public static async initialize(
    config: FeatureFlagServiceConfig
  ): Promise<FeatureFlagService> {
    if (!FeatureFlagService.instance) {
      FeatureFlagService.instance = new FeatureFlagService(config)
      await FeatureFlagService.instance.initializeClient()
    }
    return FeatureFlagService.instance
  }

  public static getInstance(): FeatureFlagService {
    if (!FeatureFlagService.instance) {
      throw new Error('FeatureFlagService has not been initialized')
    }
    return FeatureFlagService.instance
  }

  private initializeClient(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.client.on('ready', () => {
        this.initialized = true
        resolve()
      })
      this.client.on('failed', (error) => {
        this.initialized = false
        reject(new Error(`LD Client failed to initialize: ${error}`))
      })
    })
  }

  public getFlag(flagKey: string) {
    if (!this.initialized) {
      throw new Error('getFlags method: FeatureFlagService is not initialized')
    }

    return this.client.variation(flagKey)
  }

  public getAllFlags() {
    if (!this.initialized) {
      throw new Error(
        'getAllFlags method: FeatureFlagService is not initialized'
      )
    }
    return this.client.allFlags()
  }
}
